import type { IHelpdeskConfig } from "@kuika/kuika-cl-model";
import { ZendeskTicketComment } from "@kuika/kuika-cl-model";
import { EZendeskCustomField, IZendeskTicket, IZendeskUserSummary } from "@kuika/kuika-cl-model/src/models/zendesk";
import { loadHelpdeskConfig } from "../../../components/design-tools/helpdesk/helpdesk-config";
import { HelpdeskService } from "../../../services/helpdesk-service";
import ZendeskService from "../../../services/zendesk-service";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  resolveTicketLoadNotice,
  TICKET_NOT_READY_MESSAGE,
  ZendeskTicketLoadNotice
} from "../../../shared/utility/zendesk-ticket-detail";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface ZendeskProviderState {
  isZendeskWindowReload: boolean;
  zendeskTicketList: IZendeskTicket[];
  /** Display name of each ticket creator ("açan kişi"), keyed by Zendesk requester id. */
  zendeskRequesterNames: Record<number, string>;
  selectedTicket: IZendeskTicket | null;
  /** Thread of the open ticket. Fetched here, alongside the ticket itself — see `selectTicket`. */
  selectedTicketComments: ZendeskTicketComment[];
  isZendeskTicketCreating: boolean;
  /** The ticket LIST fetch. Owns the list rail's placeholder, nothing else. */
  isTicketsLoading: boolean;
  /** One ticket's detail fetch. Separate from the list so a row click leaves the rail alone. */
  isTicketDetailLoading: boolean;
  isTicketCommentsLoading: boolean;
  ticketCount: string;
  setTicketList: (tickets: IZendeskTicket[]) => void;
  setRequesterNames: (requesterNames: Record<number, string>) => void;
  onZendeskTicketSelect: (ticket: IZendeskTicket | null) => void;
  onZendeskTicketCreate: (value: boolean) => void;
  setTicketsLoading: (value: boolean) => void;
  getTicketNotifyCount: () => void;
  selectTicket: (ticketId: number) => void;
  loadTicketComments: (ticketId: number) => Promise<void>;
}

export class ZendeskProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  ZendeskProviderState
> {
  static defaultState: ZendeskProviderState = {
    isZendeskWindowReload: false,
    zendeskTicketList: [],
    zendeskRequesterNames: {},
    selectedTicket: null,
    selectedTicketComments: [],
    isZendeskTicketCreating: false,
    isTicketsLoading: false,
    isTicketDetailLoading: false,
    isTicketCommentsLoading: false,
    ticketCount: "0",
    setTicketList: () => {},
    setRequesterNames: () => {},
    onZendeskTicketSelect: () => {},
    onZendeskTicketCreate: () => {},
    setTicketsLoading: () => {},
    getTicketNotifyCount: () => {},
    selectTicket: () => {},
    loadTicketComments: () => Promise.resolve()
  };

  /**
   * The most recently requested ticket id, set synchronously when `selectTicket` starts.
   * `selectedTicket` itself isn't set until the fetch resolves, so it can't tell a stale
   * response from the current one the way `hydrateCommentPhotos`/`loadTicketComments` do —
   * this field stands in as the marker `selectTicket` commits against.
   */
  private latestTicketRequestId: number | null = null;

  getProviderValues = (): ZendeskProviderState => {
    return {
      isZendeskWindowReload: this.state.isZendeskWindowReload,
      zendeskTicketList: this.state.zendeskTicketList,
      zendeskRequesterNames: this.state.zendeskRequesterNames,
      selectedTicket: this.state.selectedTicket,
      selectedTicketComments: this.state.selectedTicketComments,
      isZendeskTicketCreating: this.state.isZendeskTicketCreating,
      isTicketsLoading: this.state.isTicketsLoading,
      isTicketDetailLoading: this.state.isTicketDetailLoading,
      isTicketCommentsLoading: this.state.isTicketCommentsLoading,
      ticketCount: this.state.ticketCount,
      setTicketList: this.setTicketList,
      setRequesterNames: this.setRequesterNames,
      onZendeskTicketSelect: this.onZendeskTicketSelect,
      onZendeskTicketCreate: this.onZendeskTicketCreate,
      setTicketsLoading: this.setTicketsLoading,
      getTicketNotifyCount: this.getTicketNotifyCount,
      selectTicket: this.selectTicket,
      loadTicketComments: this.loadTicketComments
    };
  };

  onZendeskTicketSelect = (ticket: IZendeskTicket | null) => {
    this.setState({ selectedTicket: ticket });
  };

  onZendeskTicketCreate = (value: boolean) => {
    this.setState({ isZendeskTicketCreating: value });
  };

  setTicketList = (tickets: IZendeskTicket[]) => {
    this.setState({ zendeskTicketList: tickets });
  };

  setRequesterNames = (requesterNames: Record<number, string>) => {
    this.setState({ zendeskRequesterNames: requesterNames });
  };

  setTicketsLoading = (value: boolean) => {
    this.setState({ isTicketsLoading: value });
  };

  /**
   * Opens a ticket: its detail and its thread, in ONE round-trip window.
   *
   * They used to run in series — this method fetched the ticket, committed it, and only then
   * did the freshly-mounted TicketViewer ask for the comments — so a row click cost the sum of
   * both calls (measured against the live Zendesk backend: 1.1s + 2.4s). They share nothing but
   * the id, so nothing was gained by the ordering. Comments live here rather than in the viewer
   * for the same reason: a component that mounts on the result cannot fetch in parallel with it.
   */
  selectTicket = async (ticketId: number): Promise<void> => {
    this.latestTicketRequestId = ticketId;
    try {
      // Not the list flag: a row click must not blank the rail the click came from.
      this.setState({ isTicketDetailLoading: true, isTicketCommentsLoading: true, selectedTicketComments: [] });

      const [detailResponse, commentsResponse] = await Promise.all([
        ZendeskService.GetSelectedTicket({ id: ticketId.toString() }),
        ZendeskService.GetSelectedTicketComments({ id: ticketId.toString() })
      ]);

      const ticket: IZendeskTicket | undefined = detailResponse?.data?.ticket;
      if (!ticket) {
        // Backend answered 200 but without a ticket body — treat this the same as a
        // not-yet-ready ticket and bail gracefully instead of crashing on `.ticket`.
        KMainFunctions.displayWarningNotification(TICKET_NOT_READY_MESSAGE);
        return;
      }

      // A later click may have superseded this one while it was in flight; committing then
      // would paint this ticket's detail and thread over whatever the reader opened next.
      if (this.latestTicketRequestId !== ticketId) return;

      this.setTicketList(
        this.state.zendeskTicketList.map((t) => {
          if (t.id === ticketId) {
            return {
              ...t,
              customFields: [
                ...t.customFields.filter((f) => f.id !== EZendeskCustomField.NOTIFY),
                { id: EZendeskCustomField.NOTIFY, value: false }
              ]
            };
          }

          return t;
        })
      );
      const comments: ZendeskTicketComment[] = commentsResponse?.data ?? [];
      this.setState({ selectedTicketComments: comments });
      this.onZendeskTicketSelect(ticket);
      void this.hydrateCommentPhotos(ticketId, comments);
      this.onZendeskTicketCreate(false);
      this.getTicketNotifyCount();
    } catch (e) {
      this.handleSelectTicketError(e);
    } finally {
      this.setState({ isTicketDetailLoading: false, isTicketCommentsLoading: false });
    }
  };

  /**
   * Fills each comment's author photo in AFTER the thread is on screen.
   *
   * The thread endpoint leaves `pic` empty, and the photos live behind a second call keyed by
   * author id — which is only knowable once the comments have arrived. Rather than pay that as a
   * third serial round-trip before anything paints, the thread renders on initials and the photos
   * swap in when they land; a reader loses nothing by seeing initials for a moment.
   *
   * Zendesk end-users routinely have no avatar at all (`photo` and `remotePhotoUrl` both null), so
   * the common outcome here is legitimately "no change".
   */
  private hydrateCommentPhotos = async (ticketId: number, comments: ZendeskTicketComment[]): Promise<void> => {
    const authorIds: string[] = Array.from(
      new Set(comments.map((comment: ZendeskTicketComment) => String(comment.authorId)).filter(Boolean))
    );
    if (authorIds.length === 0) return;

    try {
      const res = await ZendeskService.GetUsersPhotos(authorIds.map((id: string) => ({ id })));
      const users: IZendeskUserSummary[] | null | undefined = res?.data?.users;
      if (!Array.isArray(users)) return;

      const photoByAuthorId: Map<string, string> = new Map<string, string>();
      users.forEach((user: IZendeskUserSummary) => {
        const url: string | null | undefined = user?.photo?.contentUrl ?? user?.remotePhotoUrl;
        if (url) photoByAuthorId.set(String(user.id), url);
      });
      if (photoByAuthorId.size === 0) return;

      // The reader may have moved on while this was in flight; merging then would paint one
      // ticket's faces onto another's thread.
      if (this.state.selectedTicket?.id !== ticketId) return;

      this.setState({
        selectedTicketComments: this.state.selectedTicketComments.map((comment: ZendeskTicketComment) => {
          const pic: string | undefined = photoByAuthorId.get(String(comment.authorId));
          return pic ? { ...comment, pic } : comment;
        })
      });
    } catch {
      // A missing face is not worth a notification — the initials already stand in for it.
    }
  };

  /**
   * Re-reads the open ticket's thread on its own — the reply path, where the detail has not
   * changed and only the new message is missing.
   */
  loadTicketComments = async (ticketId: number): Promise<void> => {
    try {
      this.setState({ isTicketCommentsLoading: true });
      const res = await ZendeskService.GetSelectedTicketComments({ id: ticketId.toString() });
      const comments: ZendeskTicketComment[] = res?.data ?? [];

      // The reader may have moved on while this was in flight; merging then would paint one
      // ticket's thread onto another's.
      if (this.state.selectedTicket?.id !== ticketId) return;

      this.setState({ selectedTicketComments: comments });
      void this.hydrateCommentPhotos(ticketId, comments);
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isTicketCommentsLoading: false });
    }
  };

  /**
   * Surfaces a friendly, non-technical message when a ticket fails to open. A
   * transient 404 (the freshly-created ticket is still propagating to Zendesk)
   * is the common case and gets a softer "try again" hint; anything else gets a
   * generic failure message. The raw error/stack/response headers are never
   * shown to the user (KOA-17780).
   */
  private handleSelectTicketError = (error: unknown): void => {
    const notice: ZendeskTicketLoadNotice = resolveTicketLoadNotice(error);
    if (notice.severity === "warning") {
      KMainFunctions.displayWarningNotification(notice.message);
      return;
    }
    KMainFunctions.displayErrorNotification(notice.message);
  };

  // Kuika ITSM only: callers that ask while a badge request is in flight share it. Every Kuika user
  // shares one ITSM request budget, so a page load must not spend two calls on one number.
  private itsmUnreadRequest: Promise<number> | null = null;

  getTicketNotifyCount = async () => {
    const config: IHelpdeskConfig = await loadHelpdeskConfig();
    if (config.provider === "Itsm") {
      if (!this.itsmUnreadRequest) {
        this.itsmUnreadRequest = HelpdeskService.getUnreadCount("mine").finally((): void => {
          this.itsmUnreadRequest = null;
        });
      }
      try {
        const count: number = await this.itsmUnreadRequest;
        this.setState({ ticketCount: count.toString() });
      } catch {
        // The badge is best effort; the helpdesk itself reports errors when it is opened.
      }
      return;
    }

    if (window.location.origin.includes("kuika.com")) {
      const res = await ZendeskService.GetUserTicketNotifyCount();
      if (res && res.data) {
        this.setState({ ticketCount: res.data.toString() });
      }
    }
  };
}
